import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo
} from "react";
import { HiChevronDown, HiChevronUp, HiDownload, HiX } from "react-icons/hi";
import apiService from "../../../Services/authenticationServices";
import { useCheOrders } from "./CheOrders";
import { toast } from "react-toastify";
import split from '../../../Assets/split.svg';
import b2bService from "../../../Services/b2bServises";
import { convertUTCDate, convertAmountToWords } from "../../../Utils/utilServices";
import CureBaylogo from "../../../Assets/CurebayLogo.svg";
import bulkOrderIcon from '../../../Assets/BulkOrder.png';


const formatAddress = (...parts) => parts.filter(Boolean).join(", ");

const safeToFixed = (num, digits = 2) => {
  if (num === null || num === undefined || Number.isNaN(Number(num))) return (0).toFixed(digits);
  return Number(num).toFixed(digits);
};


const InvoiceModal = ({ isOpen, onClose, orderData, orderDetails }) => {
  if (!isOpen || !orderData) return null;

  const totalAmount = (orderDetails || []).reduce(
    (sum, item) => sum + (Number(item.unitPrice || 0) * Number(item.quantity || 0)),
    0
  );

  const discount = totalAmount - (Number(orderData.paidAmount || 0));

  const amountInWords = Number(orderData.paidAmount || 0) > 0
    ? `(${convertAmountToWords(Number(orderData.paidAmount || 0))})`
    : "";

  const printDiv = (divHtml) => {
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    if (!popupWin) {
      toast.error("Unable to open print window (popup blocked?).");
      return;
    }

    popupWin.document.write(`
      <html>
        <head>
          <title>Invoice #${orderData.orderId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; line-height: 1.5; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f6f5f1; font-weight: bold; }
          </style>
        </head>
        <body><div class="invoice-container">${divHtml}</div></body>
      </html>
    `);
    popupWin.document.close();
    popupWin.focus();
    setTimeout(() => {
      popupWin.print();
      popupWin.close();
    }, 500);
  };

  const downloadInvoice = () => {
    const printContents = document.getElementById('invoice-content')?.innerHTML;
    if (!printContents) {
      toast.error("Invoice content not ready");
      return;
    }
    printDiv(printContents);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
      <div id="invoice-content" className="bg-white rounded-lg p-4 sm:p-8 w-full max-w-7xl relative overflow-auto max-h-[90vh] print-content">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl d-print-none"
        >
          <HiX />
        </button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Order Invoice</h2>
          </div>
          <img src={CureBaylogo} alt="CureBay Logo" className="h-8" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <p className="text-gray-600">Patient</p>
            <p className="font-semibold">
              {orderData.patientName}/{orderData.patientGender}/{orderData.patientAge} Years ({orderData.patientId})
            </p>
          </div>
          <div>
            <p className="text-gray-600">Mobile</p>
            <p className="font-semibold">{orderData.patientMobile}</p>
          </div>
          <div>
            <p className="text-gray-600">Order ID</p>
            <p className="font-semibold">{orderData.orderId}</p>
          </div>
          <div>
            <p className="text-gray-600">Ordered Date</p>
            <p className="font-semibold">{convertUTCDate(orderData.modifiedDate, 'MM/DD/YYYY hh:mm:ss')}</p>
          </div>
          <div>
            <p className="text-gray-600">Transaction ID</p>
            <p className="font-semibold">{orderData.refTxnId}</p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 text-sm">Address</p>
          <p className="font-semibold text-sm">
            {formatAddress(orderData.address1, orderData.address2, orderData.patientCityBook, orderData.patientStateBook, orderData.patientPinCodeBook)}
          </p>
        </div>

        <hr className="my-4" />

        <div className="overflow-x-auto text-sm mb-6">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                {[
                  "Transaction Type",
                  "Transaction ID",
                  "Payable Amount",
                  "Date Of Payment",
                  "Payment Mode",
                  "Service Provider/Doctor",
                  "Discount Amount",
                ].map((header, idx) => (
                  <th key={idx} className="p-2 text-left border">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border">{orderData?.serviceName}</td>
                <td className="p-2 border">{orderData.refTxnId}</td>
                <td className="p-2 border">₹{safeToFixed(orderData.paidAmount)}</td>
                <td className="p-2 border">{convertUTCDate(orderData.paymentDateTime, 'MM/DD/YYYY hh:mm:ss')}</td>
                <td className="p-2 border">{orderData.payMode || "Cash"}</td>
                <td className="p-2 border">{orderData.hospitalName}</td>
                <td className="p-2 border">₹{safeToFixed(discount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="font-bold text-lg mb-2">Pharmacy Order Breakup</h3>
        <div className="overflow-x-auto text-sm mb-6">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left border">Prescription Order</th>
                <th className="p-2 text-left border">Quantity</th>
                <th className="p-2 text-left border">Unit Price</th>
                <th className="p-2 text-left border">Total Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {(orderDetails || []).map((item, index) => (
                <tr key={index}>
                  <td className="p-2 border">{item.drugName}</td>
                  <td className="p-2 border">{item.quantity}</td>
                  <td className="p-2 border">₹{safeToFixed(item.unitPrice)}</td>
                  <td className="p-2 border">₹{safeToFixed(Number(item.quantity || 0) * Number(item.unitPrice || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <div className="w-full md:w-1/2 text-sm">
            <div className="flex justify-between mb-2 font-semibold">
              <span>Total (INR):</span>
              <span>₹{safeToFixed(totalAmount)}</span>
            </div>
            <div className="flex justify-between mb-2 font-semibold">
              <span>CureBay Discount (INR):</span>
              <span>₹{safeToFixed(discount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Payable Amount (INR):</span>
              <span>₹{safeToFixed(orderData.paidAmount)}</span>
            </div>
            <p className="text-right italic text-xs text-gray-600 mt-1">{amountInWords}</p>
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-3 d-print-none">
          <button onClick={downloadInvoice} className="flex items-center gap-2 border border-blue-500 text-blue-600 px-4 py-2 rounded hover:bg-blue-100">
            <HiDownload />
            Download
          </button>
          <button onClick={onClose} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


const OrderTableRow = React.memo(({ item, dataOrderId, selectedItems, onSelectItem, onQuantityChange }) => {
  const isChecked = selectedItems.some(
    s => s.orderId === dataOrderId && s.itemId === item.itemDetailsId && s.drugCode === item.drugCode
  );

  return (
    <tr key={item.id || item.itemDetailsId} className="border-t">
      <td className="p-2 whitespace-nowrap">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onSelectItem(item, e.target.checked)}
          onMouseDown={(e) => e.stopPropagation()}
          className="cursor-pointer accent-[#1d2f5b]"
        />
      </td>

      <td className="p-2 whitespace-nowrap">{item.drugName || '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.drugCode || '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.drugsInfoComposition || '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.drugsInfoTypeofsell || '-'}</td>
      <td className="p-2 whitespace-nowrap">
        <input
          type="number"
          value={item.quantity}
          min={0}
          onChange={(e) => {
            const val = Math.max(0, parseInt(e.target.value, 10) || 0);
            onQuantityChange(item, val);
          }}
          className="w-20 border rounded px-2 py-1"
          disabled={!!item.disabled}
        />
      </td>
      <td className="p-2 whitespace-nowrap">₹{safeToFixed(item.unitPrice)}</td>
      <td className="p-2 whitespace-nowrap">₹{safeToFixed(item.actualPrice || (item.unitPrice * item.quantity || 0))}</td>
      <td className="p-2 whitespace-nowrap">{item.consignmentStatus ?? '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.deliverySlaStatus ?? '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.deliverySlaTime ?? '-'}</td>
      <td className="p-2 whitespace-nowrap">{item.deliverySlaETATime ?? '-'}</td>
      <td className="p-2 whitespace-nowrap">
        {item.consignmentId ? (
          <button type="button" className="text-blue-600 hover:underline">{item.consignmentId}</button>
        ) : '-'}
      </td>
    </tr>
  );
});


const OrderHeaderAccordion = ({
  data,
  index,
  isOpen,
  onToggle,
  selectedItems,
  onSelectItem,
  isConsignmentMode,
  onClose,
  onSelectAllItems,
  isGlobalSelectAll
}) => {
  const scrollRef = useRef(null);
  const bgColor = index % 2 === 0 ? "bg-[#f6f5f1]" : "bg-[#FFFFFF]";

  const [orderDetails, setOrderDetails] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [shouldSelectAllAfterFetch, setShouldSelectAllAfterFetch] = useState(false);

  const slaList = useMemo(() => ([
    { name: '0 -12 Hours', value: 1 },
    { name: '12-24 Hours', value: 2 },
    { name: '24-36 Hours', value: 3 },
    { name: '36-48 Hours', value: 4 },
    { name: '48-60 Hours', value: 5 },
    { name: '60-72 Hours', value: 6 },
    { name: '72 Hrs-1 Week', value: 7 },
    { name: 'Cannot be supplied', value: 8 },
    { name: 'CureBay Preferred Medicine ', value: 9 },
  ]), []);

  const pdtStatuses = useMemo(() => ([
    { name: 'Yet to Packed', value: 0 },
    { name: 'Packed', value: 1 },
    { name: 'Shipped', value: 2 },
    { name: 'Order on the Bus', value: 3 },
    { name: 'Out for Delivery', value: 4 },
    { name: 'Delivered', value: 5 }
  ]), []);

  const getStatusName = useCallback((value) => {
    const status = pdtStatuses.find(item => item.value === value);
    return status ? status.name : 'Yet to Packed';
  }, [pdtStatuses]);

  const getSlaName = useCallback((value) => {
    const slaItem = slaList.find(item => item.value === value);
    return slaItem?.name ?? '';
  }, [slaList]);

  const mapIncomingItems = useCallback((items = []) => {
    return items.map(item => ({
      id: item.itemCode || item.itemDetailsId || `${item.itemName}_${Math.random()}`,
      itemDetailsId: item.itemDetailsId,
      drugName: item.itemName || item.drugName,
      drugCode: item.itemCode,
      quantity: Number(item.quantity || 0),
      unitPrice: Number(item.price || 0) / Math.max(1, Number(item.quantity || 1)),
      consignmentStatus: item.shipmentStatus,
      totalAmount: Number(item.price || 0),
      drugsInfoComposition: item.composition || '-',
      drugsInfoTypeofsell: item.packSize || '-',
      deliverySlaStatus: item.deliverySlaStatus || '-',
      deliverySlaTime: item.deliverySlaTime || '-',
      deliverySlaETATime: item.deliverySlaETATime || '-',
      consignmentId: item.consignmentId || '-',
      // extras for split functionality:
      copiedFrom: item.copiedFrom || 'N',
      copiedqty: item.copiedqty || undefined,
      disabled: item.disabled || false,
      actualPrice: Number(item.price || 0)
    }));
  }, []);

  const fetchData = useCallback(async () => {
    try {
      if (data?.items && data.items.length > 0) {
        const transformed = mapIncomingItems(data.items);
        setOrderDetails(transformed);
      } else {
        setOrderDetails([]);
        toast.info("No order details found for this shipment.");
      }
    } catch (err) {
      console.error('Error processing order details:', err);
      toast.error("An error occurred while processing order details.");
      setOrderDetails([]);
    } finally {
      if (shouldSelectAllAfterFetch) {
        const itemsToSelect = (data?.items || []).map(item => ({
          orderId: data.orderId,
          itemId: item.itemDetailsId,
          orderDetailsId: item.itemDetailsId,
          drugCode: item.itemCode,
          quantity: item.quantity
        }));
        onSelectAllItems(data.orderId, itemsToSelect, true);
        setShouldSelectAllAfterFetch(false);
      }
    }
  }, [data, mapIncomingItems, onSelectAllItems, shouldSelectAllAfterFetch]);

  useEffect(() => {
    if (isOpen) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      fetchData();
    }
  }, [isOpen, fetchData]);

  const handleQuantityChange = useCallback((changedItem, newQty) => {
    setOrderDetails(prev => {
      const updated = prev.map(row => ({ ...row }));
      const index = updated.findIndex(r => r.itemDetailsId === changedItem.itemDetailsId && (r.copiedindex === changedItem.copiedindex || r.id === changedItem.id));
      if (changedItem.copiedFrom === 'N' && changedItem.copiedindex != null) {
        const childIndex = index;
        const originalIndex = updated.findIndex(r => r.copiedindex === undefined && r.itemDetailsId === updated[childIndex].itemDetailsId && r.copiedFrom !== 'N') || updated.findIndex((r, i) => i === updated[childIndex].copiedindex);

        const resolvedOriginalIndex = originalIndex >= 0 ? originalIndex : updated.findIndex((r, i) => i === updated[childIndex].copiedindex);

        if (resolvedOriginalIndex < 0) {
          updated[childIndex].quantity = newQty;
          updated[childIndex].actualPrice = newQty * (updated[childIndex].unitPrice || 0);
          return updated;
        }

        const child = updated[childIndex];
        const original = updated[resolvedOriginalIndex];

        const maxQty = child.copiedqty || child.quantity + original.quantity;
        if (newQty > maxQty) {
          toast.error('Split Qty greater than actual Qty');
          return prev;
        }

        child.quantity = newQty;
        child.actualPrice = newQty * (child.unitPrice || 0);

        original.quantity = maxQty - newQty;
        original.actualPrice = original.quantity * (original.unitPrice || 0);
        return updated;
      } else {
        const idx = updated.findIndex(r => r.itemDetailsId === changedItem.itemDetailsId && r.id === changedItem.id);
        if (idx < 0) return prev;
        updated[idx].quantity = newQty;
        updated[idx].actualPrice = newQty * (updated[idx].unitPrice || 0);
        return updated;
      }
    });
  }, []);


  const copyConfirmTab = useCallback((itemIndex) => {
    setOrderDetails(prev => {
      const result = prev.map(r => ({ ...r }));
      const target = result[itemIndex];
      if (!target) return prev;
      result[itemIndex].disabled = true;
      result[itemIndex].copiedFrom = 'Y';

      // Create new split entry
      const copiedItem = {
        ...target,
        id: `${target.id}_copy_${Date.now()}`,
        copiedqty: target.quantity,
        quantity: 0,
        copiedindex: itemIndex,
        disabled: false,
        copiedFrom: 'N',
        actualPrice: 0,
      };

      result.push(copiedItem);
      return result;
    });
  }, []);


  const saveChangesLocal = useCallback(async () => {
    try {
      const payload = orderDetails.map(item => ({
        itemDetailsId: item.itemDetailsId,
        quantity: Number(item.quantity || 0),
        unitPrice: Number(item.unitPrice || 0),
      }));

      const response = await apiService.updateOrderDetails(payload);
      if (response?.data?.responseCode === '200') {
        toast.success('Changes saved successfully');
        fetchData();
      } else {
        toast.error(response?.data?.message || 'Failed to save changes');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save changes');
    }
  }, [orderDetails, fetchData]);

  const isAllSelectedForThisOrder = useMemo(() => {
    const items = data.items || [];

    if (items.length === 0) return false;

    return items.every(item =>
      selectedItems.some(
        s =>
          s.orderId === data.orderId &&
          s.itemId === item.itemDetailsId &&
          s.drugCode === item.itemCode
      )
    );
  }, [selectedItems, data.items, data.orderId]);


  const handleHeaderCheckboxChange = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;

    if (checked && orderDetails.length === 0) {
      setShouldSelectAllAfterFetch(true);
      fetchData();
      return;
    }

    const itemsToSelect = orderDetails.map(item => ({
      orderId: data.orderId,
      itemId: item.itemDetailsId,
      orderDetailsId: item.itemDetailsId,
      drugCode: item.drugCode,
      quantity: item.quantity
    }));

    onSelectAllItems(data.orderId, itemsToSelect, checked);
  };

  const headerFields = [
    {
      label: "",
      value: (
        <input
          type="checkbox"
          checked={isAllSelectedForThisOrder}
          onChange={handleHeaderCheckboxChange}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ),
      width: "sm:w-[3%]"
    },
    { label: "Shipment No", value: data.shipmentPackageNo, width: "sm:w-[12%]" },
    { label: "Order ID", value: data.orderId, width: "sm:w-[12%]" },
    { label: "Patient Name", value: data.patientName, width: "sm:w-[12%]" },
    { label: "Mobile No.", value: data.patientMobile, width: "sm:w-[12%]" },
    { label: "Patient Address", value: formatAddress(data.address), width: "sm:w-[20%]" },
    { label: "Eclinic", value: data.cheBranchName ?? "Null", width: "sm:w-[10%]" },
  ];

  const columns = [
    { field: 'checkBox', header: '' },
    { field: 'drugName', header: 'Drug Name' },
    { field: 'drugCode', header: 'Drug Code' },
    { field: 'drugsInfoComposition', header: 'Composition & Strength' },
    { field: 'drugsInfoTypeofsell', header: 'Package Size' },
    { field: 'quantity', header: 'Quantity' },
    { field: 'unitPrice', header: 'Unit Price' },
    { field: 'actualPrice', header: 'Payable Amount' },
    { field: 'consignmentStatus', header: 'Pdt.Status' },
    { field: 'deliverySlaStatus', header: 'SLA' },
    { field: 'deliverySlaTime', header: 'Committed ETA' },
    { field: 'deliverySlaETATime', header: 'ETA' },
    { field: 'consignmentId', header: 'Consignment ID' },
  ];

  return (
    <div ref={scrollRef} className={`border rounded ${bgColor} my-3`}>
      <div className="cursor-pointer px-2 sm:px-4 flex justify-between items-center">
        <div className="flex-1 sm:flex-initial min-w-0 flex flex-wrap sm:flex-nowrap overflow-auto text-xs p-2 sm:p-4 gap-x-3 gap-y-2 sm:gap-5">
          {headerFields.map(({ label, value, width }, i) => (
            <span key={i} className={`w-[calc(50%-6px)] ${width} break-words`} onClick={i === 0 ? undefined : () => onToggle()}>
              <span className="text-gray-500">{label}</span> {value}
            </span>
          ))}
        </div>
        <span className="text-gray-600 text-xl" onClick={() => onToggle()}>
          {isOpen ? <HiChevronUp /> : <HiChevronDown />}
        </span>
      </div>

      {isOpen && <div className="h-[1px] bg-gray-300 w-full" />}

      {isOpen && (
        <div className="p-4 pb-8 bg-white overflow-auto">
          <div className="flex flex-wrap sm:flex-nowrap overflow-auto gap-4 mb-4 text-sm">
            {data.defaultSla && (
              <div className="w-full sm:w-[15%] break-words">
                <p className="text-gray-500">Delivery SLA: <span> {getStatusName(data.defaultSla)}</span></p>
              </div>
            )}
            {data.shipTo && <div className="w-full sm:w-[15%] break-words"><p className="text-gray-500">Ship To: <span>{data.shipTo}</span></p></div>}
            {data.shippingName && <div className="w-full sm:w-[20%] break-words"><p className="text-gray-500">Shipping Name: <span>{data.shippingName}</span></p></div>}
            {data.shippingAddress && <div className="w-full sm:w-[50%] break-words"><p className="text-gray-500">Shipping Address: <span>{data.shippingAddress}</span></p></div>}
          </div>

          <div className="flex flex-wrap justify-between gap-2 mb-3">
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md" onClick={() => setShowInvoiceModal(true)}>
              Open Invoice
            </button>

            <div className="flex flex-wrap items-center gap-6">
              {data.bulkOrderFlag === "Y" && (
                <div className="flex items-center text-[#2e4373] font-medium">
                  <img src={bulkOrderIcon} alt="Bulk Order Icon" className="h-6 w-6 mr-1" />
                  Marked as Bulk Order
                </div>
              )}

              <button className="bg-[#336b3b] text-white px-4 py-2 rounded-md" onClick={saveChangesLocal}>
                Save Changes
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-[#1d2f5b] rounded-md">
            <div className="min-w-full inline-block align-middle">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-[#1d2f5b] text-white">
                    <tr>
                      {columns.map((col, idx) => <th key={idx} className="p-2 font-semibold">{col.header}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(orderDetails || []).map((item, idx) => (
                      <OrderTableRow
                        key={item.id || idx}
                        item={item}
                        dataOrderId={data.orderId}
                        selectedItems={selectedItems}
                        onSelectItem={(it, checked) => onSelectItem({
                          orderId: data.orderId,
                          itemId: it.itemDetailsId,
                          orderDetailsId: it.itemDetailsId,
                          drugCode: it.drugCode,
                          quantity: it.quantity
                        }, checked, data.orderId)}
                        onQuantityChange={(it, qty) => handleQuantityChange(it, qty)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        orderData={data}
        orderDetails={orderDetails}
      />
    </div>
  );
};


const CheShipmentRecevied = forwardRef((props, ref) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isConsignmentMode, setIsConsignmentMode] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const { filter, setFilterField } = useCheOrders();
  const [isGlobalSelectAll, setIsGlobalSelectAll] = useState(false);
  const pageSizeDefault = 20;

  const hasOrderId = Boolean(filter.orderId && filter.orderId.trim() !== "");
  const hasEnquiryId = Boolean(filter.enquiryId && filter.enquiryId.trim() !== "");
  const hasEclinicCode = Boolean(filter.eclinicCode && filter.eclinicCode.trim() !== "");

  const showSelectAll = hasOrderId || hasEnquiryId || hasEclinicCode;

  const selectedIdLabel = hasOrderId
    ? filter.orderId
    : hasEnquiryId
      ? filter.enquiryId
      : hasEclinicCode
        ? filter.eclinicCode
        : "";

  const fetchData = useCallback(async (pageNo = 1) => {
    try {
      setSelectedItems([]);
      setIsGlobalSelectAll(false);

      const isOrderSearch = showSelectAll;

      const payload = {
        pageNumber: pageNo,
        pageSize: isOrderSearch ? 100 : pageSizeDefault,
        sortBy: sortOrder === 0 ? 'ASC' : 'DESC',
        searchText: filter.orderId || "",
        circleCode: "N",
        state: "OD",
        ...Object.fromEntries(
          Object.entries(filter).filter(([k, v]) => v !== null && v !== undefined && v !== "" && k !== "orderId")
        ),
      };

      if (payload.fromDate && payload.toDate) {
        payload.fromDate = convertUTCDate(payload.fromDate);
        payload.toDate = convertUTCDate(payload.toDate);
      }

      const response = await apiService.getShipmentList(payload);
      if (response?.data?.shipments) {
        setOrders(response.data.shipments);
        setCurrentPage(pageNo);
        setHasNextPage(pageNo < response.data.totalPages);
      } else {
        setOrders([]);
        setHasNextPage(false);
      }
    } catch (err) {
      console.error("Failed to fetch orders", err);
      setOrders([]);
      setHasNextPage(false);
      toast.error("Failed to fetch orders");
    }
  }, [filter, sortOrder, showSelectAll]);

  useImperativeHandle(ref, () => ({
    onSearch: () => fetchData(1)
  }), [fetchData]);

  useEffect(() => {
    fetchData(1);
  }, [filter, sortOrder, fetchData]);

  const handleToggle = useCallback((index) => {
    setOpenIndex(prev => (prev === index ? null : index));
  }, []);

  const handleSortChange = (value) => {
    setSortOrder(value);
  };


  // item payload should be shaped as { orderId, itemId, orderDetailsId, drugCode, quantity }
  const handleSelectItem = (item, isChecked) => {
    if (!isChecked) {
      setIsGlobalSelectAll(false);
    }

    setSelectedItems(prev => {
      if (isChecked) {
        const exists = prev.some(s => s.orderId === item.orderId && s.itemId === item.itemId);
        if (exists) return prev;
        return [...prev, item];
      } else {
        return prev.filter(s => !(s.orderId === item.orderId && s.itemId === item.itemId));
      }
    });
  };


  const handleSelectAllItems = (orderId, orderDetails, isChecked) => {
    setSelectedItems(prev => {
      if (isChecked) {
        const withoutThisOrder = prev.filter(item => item.orderId !== orderId);
        const normalized = orderDetails.map(it => ({
          orderId,
          itemId: it.itemId || it.orderDetailsId,
          orderDetailsId: it.orderDetailsId || it.itemId,
          drugCode: it.drugCode,
          quantity: it.quantity
        }));
        return [...withoutThisOrder, ...normalized];
      } else {
        return prev.filter(item => item.orderId !== orderId);
      }
    });
  };

  const handleSelectAll = (checked) => {
    setIsGlobalSelectAll(checked);
    if (checked) {
      const allItems = orders.flatMap(order =>
        (order.items || []).map(item => ({
          orderId: order.orderId,
          itemId: item.itemDetailsId,
          modifiedBy: localStorage.getItem('userCode'),
          orderDetailsId: item.itemDetailsId,
          drugCode: item.itemCode,
          quantity: item.quantity
        }))
      );
      setSelectedItems(allItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleCreateConsignment = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to create consignment");
      return;
    }

    try {
      const transformedItems = selectedItems.map(item => ({
        orderId: item.orderId,
        itemId: item.itemId || item.orderDetailsId,
        modifiedBy: localStorage.getItem('userCode'),
        orderDetailsId: item.orderDetailsId,
        drugCode: item.drugCode,
        quantity: item.quantity
      }));

      const response = await b2bService.createConsignment(transformedItems, 'CHE');

      if (response?.responseCode === '200') {
        toast.success('Consignment created successfully.');
        setSelectedItems([]);
        await packConsignment(response.successObject.consignmentId);
        await fetchData(currentPage);
      } else {
        toast.error(response?.responseMessage || "Failed to create consignment");
      }
    } catch (error) {
      console.error("Consignment creation error:", error);
      toast.error("Failed to create consignment");
    }
  };

  const packConsignment = async (consignmentId) => {
    if (!consignmentId) {
      toast.error('Please select consignment id');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('loginObj') || '{}');
      const payload = {
        consignmentId,
        statusName: "PACKED",
        userName: `${user.salutation || ''}${user.firstName || ''}${user.lastName || ''}`,
        userId: localStorage.getItem('userCode'),
        createdBy: localStorage.getItem('userCode'),
        modifiedBy: localStorage.getItem('userCode'),
        status: "1",
        source: 'B2B2C'
      };

      const response = await b2bService.packConsignment(payload);
      if (response?.responseCode === '200') {
        toast.success('Status Updated Successfully.');
        fetchData(currentPage);
        const x = openIndex;
        setOpenIndex(null);
        setTimeout(() => setOpenIndex(x), 500);
      } else {
        toast.error(response?.message || 'Failed to update packing status');
      }
    } catch (error) {
      console.error("Packing error:", error);
      toast.error("Failed to pack consignment");
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      fetchData(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) fetchData(currentPage - 1);
  };

  return (
    <div className="pl-1 pr-1 sm:pr-6 min-h-screen">
      <div className="flex flex-wrap items-center gap-4 ml-4 mb-4">
        <label className="flex items-center gap-2">
          <input type="radio" name="sortOrder" value="newest" checked={sortOrder === 1} onChange={() => handleSortChange(1)} className="h-4 w-4 text-blue-600" />
          <span>Newest First</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name="sortOrder" value="oldest" checked={sortOrder === 0} onChange={() => handleSortChange(0)} className="h-4 w-4 text-blue-600" />
          <span>Oldest First</span>
        </label>
      </div>

      <div className="flex flex-wrap justify-end mb-3 gap-2">
        {showSelectAll && (
          <div className="ml-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isGlobalSelectAll} onChange={(e) => handleSelectAll(e.target.checked)} className="h-4 w-4 cursor-pointer accent-[#1d2f5b]" />
              <span className="text-sm font-medium">
                <span className="text-gray-500">Select all shipments for </span>
                <span className="text-blue-600">{selectedIdLabel}</span>
              </span>
            </label>
          </div>
        )}

        <div className="ml-auto">
          <button
            className="bg-[#336b3b] hover:bg-[#336b3b] text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center gap-1"
            onClick={handleCreateConsignment}
            disabled={selectedItems.length === 0}
          >
            Create Consignment
            {selectedItems.length > 0 && (
              <span className="bg-white text-[#336b3b] rounded-full w-5 h-5 flex items-center justify-center text-xs ml-1">
                {selectedItems.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {orders.map((order, index) => (
        <OrderHeaderAccordion
          key={order.orderId || index}
          data={order}
          index={index}
          isOpen={openIndex === index}
          onToggle={() => handleToggle(index)}
          selectedItems={selectedItems}
          onSelectItem={(payload, checked) => handleSelectItem(payload, checked)}
          isConsignmentMode={isConsignmentMode}
          onClose={() => fetchData(currentPage)}
          onSelectAllItems={handleSelectAllItems}
          isGlobalSelectAll={isGlobalSelectAll}
        />
      ))}

      <div className="flex flex-wrap justify-center items-center mt-4 mb-8 gap-2">
        <button onClick={handlePrevPage} disabled={currentPage === 1} className="px-4 py-2 border rounded disabled:opacity-50">Previous</button>

        <button onClick={() => fetchData(1)} className={`px-4 py-2 border rounded ${currentPage === 1 ? 'bg-blue-100' : ''}`}>1</button>

        {currentPage > 1 && (
          <button onClick={() => fetchData(currentPage)} className="px-4 py-2 border rounded bg-blue-100">{currentPage}</button>
        )}

        <button onClick={handleNextPage} disabled={!hasNextPage} className="px-4 py-2 border rounded disabled:opacity-50">Next</button>
      </div>
    </div>
  );
});

export default CheShipmentRecevied;
